import { cmpByteArray, compareByteArray, concatByteArray } from '../utility';
import { AbstractKeyring, MissingKey } from './base';

/**
 * The account keyring has a trust state that must be loaded, validated checked before using them.
 * It declares the key version that are used for public-key cryptography.
 */
export abstract class AbstractAccountKeyring extends AbstractKeyring {
  public static TRUSTED_NOT = 0; // verification failed, keyring *is* compromised
  public static TRUSTED_UNKNOWN = 1; // not trusted
  public static TRUSTED_FULLY = 2; // trusted and fingerprint is of the most preferred key
  public static TRUSTED_STALE = 3; // trusted, but fingerprint is of an older key (no newer is available for the account)
  public static TRUSTED_REQUIRES_UPGRADE = 4; // trusted, but fingerprint should be upgraded

  public id;
  public _public_keys;
  public trusted;

  // safe_radix32.encode_fw(0)
  public static ANONYMOUS_ID = '2222222222222';
  public static ANONYMOUS_ID_B = new TextEncoder().encode('2222222222222');

  // real key versions
  public static CURVE25519 = '1';
  public static ED25519 = '2';
  public static MLKEM1024 = '4'; // PK to use for KEM send
  public static MLDSA87 = '5';

  // in order of preference (from best to oldest)
  public static TRUST_KEY_VERSIONS = [
    AbstractAccountKeyring.MLDSA87,
    AbstractAccountKeyring.ED25519,
  ];

  public static VALID_PUBLIC_KEY_LENGTH_MAP = {
    [AbstractAccountKeyring.CURVE25519]: 32,
    [AbstractAccountKeyring.ED25519]: 32,
    [AbstractAccountKeyring.MLKEM1024]: 1568,
    [AbstractAccountKeyring.MLDSA87]: 2592,
  };

  public static VARIANT_BASE = '1'; // EC cryptography keyring
  public static VARIANT_ML = '2'; // EC+ML (pqc) cryptography keyring
  public static VARIANTS = [AbstractAccountKeyring.VARIANT_ML, AbstractAccountKeyring.VARIANT_BASE];

  //public static abstract load: Function

  constructor(account_id: string, public_keys: Object, trusted?: number) {
    super();
    this.id = new TextEncoder().encode(account_id);
    AbstractAccountKeyring.check_key_lengths(
      public_keys,
      AbstractAccountKeyring.VALID_PUBLIC_KEY_LENGTH_MAP
    );
    this._public_keys = public_keys;
    this.trusted =
      trusted === undefined || trusted === null ? AbstractAccountKeyring.TRUSTED_UNKNOWN : trusted;
  }

  public get_public_key(version: string): Uint8Array {
    let key = this._public_keys[version];
    if (key) return key;
    else {
      throw new MissingKey(version + '');
    }
  }

  public is_anonymous(): boolean {
    return compareByteArray(this.id, AbstractAccountKeyring.ANONYMOUS_ID_B);
  }

  public get_variant(): string {
    if (
      this._public_keys[AbstractAccountKeyring.MLKEM1024] ||
      this._public_keys[AbstractAccountKeyring.MLDSA87]
    ) {
      return AbstractAccountKeyring.VARIANT_ML;
    }
    return AbstractAccountKeyring.VARIANT_BASE;
  }

  public get_trust_root(version: string): Uint8Array {
    if (version == AbstractAccountKeyring.MLDSA87) {
      // This is an extension to the base ED25519 key, so we fingerprint both (hybrid).
      return concatByteArray(
        this.get_public_key(AbstractAccountKeyring.ED25519),
        this.get_public_key(AbstractAccountKeyring.MLDSA87)
      );
    } else if (version == AbstractAccountKeyring.ED25519) {
      return this.get_public_key(AbstractAccountKeyring.ED25519);
    }
    throw new MissingKey(version + '');
  }

  /**
   * The actual order is not relevant, the goal is for it to be deterministic.
   */
  public ordered_ids(other: AbstractAccountKeyring): Uint8Array {
    if (cmpByteArray(this.id, other.id) < 0) {
      return concatByteArray(this.id, other.id);
    }
    return concatByteArray(other.id, this.id);
  }
}

/**
 * Add TOFU (trust-on-first-use) identity generation and storage to the peer keyring.
 * Shared secrets from KEMs are stored on this as well.
 */
export abstract class AbstractPeerAccountKeyring extends AbstractAccountKeyring {
  public tofu_fingerprints;
  public _shared_secrets;

  public static SS_SELF_MLKEM1024 = '6'; // SS from KEM sent by self
  public static SS_PEER_MLKEM1024 = '7'; // SS from KEM received from peer

  public static VALID_SHARED_SECRET_LENGTH_MAP = {
    [AbstractPeerAccountKeyring.SS_SELF_MLKEM1024]: 32,
    [AbstractPeerAccountKeyring.SS_PEER_MLKEM1024]: 32,
  };

  constructor(
    account_id: string,
    public_keys: Object,
    trusted?: number,
    shared_secrets?: { [key: string]: Uint8Array }
  ) {
    if (!shared_secrets) {
      shared_secrets = {};
    }
    super(account_id, public_keys, trusted);
    AbstractSelfAccountKeyring.check_key_lengths(
      shared_secrets,
      AbstractPeerAccountKeyring.VALID_SHARED_SECRET_LENGTH_MAP
    );
    this.tofu_fingerprints = null;
    this._shared_secrets = shared_secrets;
  }

  /**
     * Get the simple fingerprints of the trust keys of the keyring. These are stored on the client side
        and compared automatically when re-loading the same keyring for consistency.
     */
  public async create_tofu_fingerprints(): Promise<void> {
    this.tofu_fingerprints = {};
    for (let key_version of AbstractAccountKeyring.TRUST_KEY_VERSIONS) {
      let key;
      try {
        key = this.get_trust_root(key_version);
      } catch (e) {
        if (e instanceof MissingKey) {
          continue;
        }
      }
      let digest = await crypto.subtle.digest('SHA-256', key);
      this.tofu_fingerprints[key_version] = new Uint8Array(digest.slice(0, 16));
    }
  }

  public get_shared_secret(version: string): Uint8Array {
    let key = this._shared_secrets[version];
    if (key) return key;
    else {
      throw new MissingKey(version + '');
    }
  }

  public abstract update_shared_secrets(
    self_kr: AbstractSelfAccountKeyring,
    mlkem1024_ss_self?: Uint8Array, // generated and encrypted by self_kr
    mlkem1024_ss_peer?: Uint8Array, // generated (+_sig) by peer; later encrypted by self_kr (optimized)
    mlkem1024_ss_peer_sig?: Uint8Array // signature by peer
  ): Promise<boolean>;
}

/**
 * The self account can have the same public cryptographic items as a peer and their secret counterparts.
    Additionally secret symmetric keys could be here. The an account's own keyring is always trusted (see notes
    at the implementation of load()).
 */
export abstract class AbstractSelfAccountKeyring extends AbstractAccountKeyring {
  public _secret_keys;

  public static MASTER = '3';

  public static VALID_SECRET_KEY_LENGTH_MAP = Object.assign(
    {},
    AbstractAccountKeyring.VALID_PUBLIC_KEY_LENGTH_MAP,
    {
      [AbstractSelfAccountKeyring.MASTER]: 32,
      [AbstractAccountKeyring.MLKEM1024]: 3168,
      [AbstractAccountKeyring.MLDSA87]: 4896,
    }
  );

  //public static abstract load: Function;

  constructor(account_id: string, public_keys: Object, secret_keys: Object) {
    super(account_id, public_keys, AbstractSelfAccountKeyring.TRUSTED_FULLY);
    AbstractSelfAccountKeyring.check_key_lengths(
      secret_keys,
      AbstractSelfAccountKeyring.VALID_SECRET_KEY_LENGTH_MAP
    );
    this._secret_keys = secret_keys;
  }

  public static load: Function;

  public update(public_keys?: Object, secret_keys?: Object) {
    if (public_keys) {
      AbstractSelfAccountKeyring.check_key_lengths(
        public_keys,
        AbstractSelfAccountKeyring.VALID_PUBLIC_KEY_LENGTH_MAP
      );
      this._public_keys = Object.assign(this._public_keys, public_keys);
    }
    if (secret_keys) {
      AbstractSelfAccountKeyring.check_key_lengths(
        secret_keys,
        AbstractSelfAccountKeyring.VALID_SECRET_KEY_LENGTH_MAP
      );
      this._secret_keys = Object.assign(this._secret_keys, secret_keys);
    }
  }

  public get_secret_key(version: string): Uint8Array {
    let key = this._secret_keys[version];
    if (key) {
      return key;
    } else {
      throw new MissingKey(version + '');
    }
  }

  public get_variant(): string {
    if (
      this._secret_keys[AbstractAccountKeyring.MLKEM1024] ||
      this._secret_keys[AbstractAccountKeyring.MLDSA87]
    ) {
      return AbstractAccountKeyring.VARIANT_ML;
    }
    return super.get_variant();
  }
}
